import _ from '@/setup/lodash';
import { type RootState } from '@/store';
import i18n from '@/i18n';
import { useApi } from '@/api';

import {
  AUTH_REQUEST,
  AUTH_ERROR,
  AUTH_SUCCESS,
  AUTH_SAVE_TOKEN,
  AUTH_BY_TOKEN,
  AUTH_LOGOUT,
  AUTH_2FA_REQUEST,
  AUTH_2FA_SUCCESS,
  AUTH_2FA_ERROR,
  AUTH_2FA_RESET,
  AUTH_QR_SET,
  AUTH_ADMIN_INFO,
  AUTH_ADMIN_ACL,
  AUTH_CURRENT_TEAM,
  AUTH_CURRENT_ACL,
  CHANGE_ENOUGH_PERMISSIONS,
} from '../action_types/auth';
import type { ActionContext } from 'vuex';
import type { GetAclResponse, GetProfileResponse, PostAuthLoginData, PostAuthLoginResponse, PostAuthTotpData } from '@/types/openapi-ts';
import type { TypedStoreModule } from '@/types/vuex';
import { bindLsUser } from '@/setup/vueLs';

export interface AuthState {
  token: string,
  status: string,
  status2fa: string,
  qrSrc: string,

  adminAcl: GetAclResponse['payload'],
  adminInfo: GetProfileResponse['payload'],
  currentTeam: string,
  currentAcl: GetAclResponse['payload']['permissions']['someStringKey'],
}

const state: AuthState = {
  token: localStorage.getItem('auth-token') || '',
  status: localStorage.getItem('auth-token') ? 'success' : '',
  status2fa: localStorage.getItem('status2fa') || '',
  qrSrc: '',

  //@ts-expect-error инициализация без проверки типа
  adminAcl: {},
  //@ts-expect-error инициализация без проверки типа
  adminInfo: {},
  currentTeam: '',
  //@ts-expect-error инициализация без проверки типа
  currentAcl: {},
}

const getters = {
  isAuthenticated: (state: AuthState) => state.status === 'success' && state.status2fa === 'success',
  authStatus: (state: AuthState) => state.status,
  tfaStatus: (state: AuthState) => state.status2fa,
  qrSrc: (state: AuthState) => state.qrSrc,
  token: (state: AuthState) => state.token,

  adminInfo: (state: AuthState) => state.adminInfo,
  adminAcl: (state: AuthState) => state.adminAcl,
  currentTeam: (state: AuthState) => state.currentTeam,
  currentAcl: (state: AuthState) => state.currentAcl,
};

const actions = {
  [AUTH_REQUEST]: async ({ commit }: ActionContext<AuthState, RootState>, payload: PostAuthLoginData['body']) => {
    const api = useApi();

    commit(AUTH_REQUEST);
    try {
      const response = await api.signIn(payload);
      if (response.data.success) {
        commit(AUTH_SUCCESS);
        api.affiliatesApi.defaults.headers.common.Authorization = response.data.payload.id;
        api.miscApi.defaults.headers.common.Authorization = response.data.payload.id;
        commit(AUTH_SAVE_TOKEN, response.data.payload);
        if (response.data.payload.otp_auth_url && response.data.payload.otp_qr_code_url) {
          commit(AUTH_QR_SET, response.data.payload.otp_qr_code_url);
        }
        return response.data.payload;
      }
      commit(AUTH_ERROR);
      throw new Error('AUTH ERROR');
    } catch (error: any) {
      if (error.data.errors) {
        const errorMessage = error.data.errors;
        commit(AUTH_ERROR);
        throw errorMessage;
      }

      if (error.data.errors === null) {
        const errorMessage = {
          message: error.data.message,
          email: [{
            message: error.data.message,
            code: error.data.code,
          }],
          password: [{
            message: error.data.message,
            code: error.data.code,
          }],
        };
        commit(AUTH_ERROR);
        throw errorMessage;
      }
      commit(AUTH_ERROR);
      throw error;
    }
  },
  [AUTH_LOGOUT]: async ({ commit }: ActionContext<AuthState, RootState>) => {
    const api = useApi();

    try {
      await api.signOut();
      commit(AUTH_LOGOUT);
      return;
    } catch (error) {
      commit(AUTH_LOGOUT);
      throw error;
    }
  },
  [AUTH_2FA_REQUEST]: async ({ commit }: ActionContext<AuthState, RootState>, payload: PostAuthTotpData['body']) => {
    const api = useApi();

    try {
      const { data: { payload: response } } = await api.confirm2fa(payload);

      if (response.result) {
        return response;
      }
      commit(AUTH_2FA_ERROR);
      const errorMessage = i18n.global.messages.value[i18n.global.locale.value].validation.errors[2006];
      const er = {
        message: 'WRONG 2FA CODE',
        token: [
          {
            message: errorMessage,
            code: 2006,
          },
        ],
      };
      throw er;
    } catch (error) {
      commit(AUTH_2FA_ERROR);
      throw error;
    }
  },
  [AUTH_2FA_RESET]: ({ commit }: ActionContext<AuthState, RootState>) => {
    commit(AUTH_2FA_RESET);
  },
  [AUTH_ADMIN_INFO]: async ({ commit }: ActionContext<AuthState, RootState>) => {
    const api = useApi();

    const response = await api.getAdminInfo();
    commit(AUTH_ADMIN_INFO, response.data.payload);
  },
  [AUTH_ADMIN_ACL]: async ({ commit, state }: ActionContext<AuthState, RootState>) => {
    const api = useApi();
    const response = await api.getAcl();
    commit(AUTH_ADMIN_ACL, response.data.payload);
    const findTeam = _.find(state.adminAcl.teams, ['id', localStorage.getItem('active-team')]);
    if (state.adminAcl.is_superuser) {
      if ((localStorage.getItem('active-team') && findTeam === undefined) || localStorage.getItem('active-team') === null) {
        localStorage.setItem('active-team', '');
      }
    } else if (!state.adminAcl.is_superuser && state.adminAcl.teams.length === 0) {
      localStorage.setItem('active-team', '');
    } else if (!localStorage.getItem('active-team') || findTeam === undefined || localStorage.getItem('active-team') === null) {
      localStorage.setItem('active-team', state.adminAcl.teams![0]!.id);
    }
    if (localStorage.getItem('active-team') && localStorage.getItem('active-team') !== null) {
      commit(AUTH_CURRENT_TEAM, localStorage.getItem('active-team'));
      commit(AUTH_CURRENT_ACL, state.adminAcl.permissions[
        localStorage.getItem('active-team') as keyof typeof state.adminAcl.permissions
      ]);
    }
    api.affiliatesApi.defaults.headers.common['X-Team-Id'] = localStorage.getItem('active-team') as string;
  },

  [CHANGE_ENOUGH_PERMISSIONS]: async ({ commit }: ActionContext<AuthState, RootState>) => {
    const api = useApi();
    const { data: { payload } } = await api.getAcl();
    commit(AUTH_ADMIN_ACL, payload);
    const findTeam = _.find(payload.teams, ['id', localStorage.getItem('active-team')]);
    if (payload.is_superuser) {
      if ((localStorage.getItem('active-team') && findTeam === undefined) || localStorage.getItem('active-team') === null) {
        localStorage.setItem('active-team', '');
      }
    } else if (!payload.is_superuser && payload.teams.length === 0) {
      localStorage.setItem('active-team', '');
    } else if (!localStorage.getItem('active-team') || findTeam === undefined || localStorage.getItem('active-team') === null) {
      localStorage.setItem('active-team', payload.teams![0]!.id);
    }
    commit(AUTH_CURRENT_TEAM, localStorage.getItem('active-team'));
    commit(AUTH_CURRENT_ACL, localStorage.getItem('active-team') 
      ? payload.permissions[localStorage.getItem('active-team') as keyof typeof payload.permissions] 
      : {}
    );
  },

}

const mutations = {
  [AUTH_REQUEST]: (state: AuthState) => {
    state.status = 'loading';
  },
  [AUTH_SUCCESS]: (state: AuthState) => {
    state.status = 'success';
  },
  [AUTH_SAVE_TOKEN]: (state: AuthState, response: PostAuthLoginResponse['payload']) => {
    state.token = response.id;
    localStorage.setItem('auth-token', response.id);
  },
  [AUTH_ERROR]: (state: AuthState) => {
    localStorage.removeItem('auth-token');
    state.status = 'error';
  },
  [AUTH_LOGOUT]: (state: AuthState) => {
    localStorage.removeItem('auth-token');
    localStorage.removeItem('status2fa');
    state.token = '';
    state.status = '';
    state.status2fa = '';
  },
  [AUTH_2FA_REQUEST]: (state: AuthState) => {
    state.status2fa = 'loading';
  },
  [AUTH_2FA_SUCCESS]: (state: AuthState) => {
    localStorage.setItem('status2fa', 'success');
    state.status2fa = 'success';
  },
  [AUTH_2FA_ERROR]: (state: AuthState) => {
    localStorage.removeItem('status2fa');
    state.status2fa = 'error';
  },
  [AUTH_BY_TOKEN]: (state: AuthState, token: string) => {
    localStorage.setItem('auth-token', token);
    localStorage.setItem('status2fa', 'success');
    state.status = 'success';
    state.status2fa = 'success';
    state.token = token;
  },
  [AUTH_2FA_RESET]: (state: AuthState) => {
    localStorage.removeItem('status2fa');
    state.status2fa = '';
  },
  [AUTH_QR_SET]: (state: AuthState, qrSrc: string) => {
    state.qrSrc = qrSrc;
  },


  [AUTH_ADMIN_INFO]: (state: AuthState, payload: GetProfileResponse['payload']) => {
    bindLsUser(payload.id);
    state.adminInfo = _.cloneDeep(payload);
  },

  [AUTH_ADMIN_ACL]: (state: AuthState, payload: GetAclResponse['payload']) => {
    state.adminAcl = _.cloneDeep(payload);
  },

  [AUTH_CURRENT_TEAM]: (state: AuthState, payload: string) => {
    state.currentTeam = payload;
  },

  [AUTH_CURRENT_ACL]: (state: AuthState, payload: GetAclResponse['payload']['permissions']['someStringKey']) => {
    state.currentAcl = payload;
  },
}

const storeModule = {
  state, 
  getters,
  actions,
  mutations,
  namespaced: true,
}

export type AuthModule = TypedStoreModule<'auth', typeof storeModule>;

export default storeModule;
